import { api } from "lib/graphql/api"
import { gql } from "@apollo/client"
import { query } from "lib/graphql/query"
import { mutate } from "lib/graphql/mutate"

export const getShareLinkScheduleComments = api(async function listShareLinkComments({
    shareLinkId,
    regimeId,
    scheduleId,
    schedulePath,
}) {
    if (!shareLinkId || !regimeId || !scheduleId) return []
    const result = await query(
        gql`
            query ListShareLinkComments(
                $shareLinkId: String!
                $regimeId: String!
                $scheduleId: String!
                $schedulePath: String
            ) {
                listShareLinkComments(
                    shareLinkId: $shareLinkId
                    regimeId: $regimeId
                    scheduleId: $scheduleId
                    schedulePath: $schedulePath
                ) {
                    comments {
                        _id
                        threadId
                        parentId
                        linkId
                        regimeId
                        scheduleId
                        schedulePath
                        scheduleTitle
                        content
                        authorScope
                        createdBy
                        authorName
                        versionLabel
                        created
                        modified
                        resolved
                        resolvedBy
                        resolvedAt
                        deleted
                        editedAt
                        selectionMetadata {
                            path
                            parentId
                            taskIndex
                            part
                            partLabel
                            start
                            end
                            selectedText
                            beforeText
                            afterText
                        }
                    }
                }
            }
        `,
        { shareLinkId, regimeId, scheduleId, schedulePath },
        { returns: "listShareLinkComments" }
    )
    return buildCommentThreads(result?.comments ?? [])
})

// Internal reviewer query: all comments for a schedule across EVERY share link in the regime, with no
// share-link id needed. The server gates this on regime-edit permission, so recipients get nothing.
// This is what makes the per-schedule Comments panel work for internal users without URL parameters.
export const getRegimeScheduleComments = api(async function listRegimeScheduleComments({
    regimeId,
    scheduleId,
    schedulePath,
}) {
    if (!regimeId || !scheduleId) return []
    const result = await query(
        gql`
            query ListRegimeScheduleComments($regimeId: String!, $scheduleId: String!, $schedulePath: String) {
                listRegimeScheduleComments(regimeId: $regimeId, scheduleId: $scheduleId, schedulePath: $schedulePath) {
                    comments {
                        _id
                        threadId
                        parentId
                        linkId
                        regimeId
                        scheduleId
                        schedulePath
                        scheduleTitle
                        content
                        authorScope
                        createdBy
                        authorName
                        versionLabel
                        created
                        modified
                        resolved
                        resolvedBy
                        resolvedAt
                        deleted
                        editedAt
                        selectionMetadata {
                            path
                            parentId
                            taskIndex
                            part
                            partLabel
                            start
                            end
                            selectedText
                            beforeText
                            afterText
                        }
                    }
                }
            }
        `,
        { regimeId, scheduleId, schedulePath },
        { returns: "listRegimeScheduleComments" }
    )
    return buildCommentThreads(result?.comments ?? [])
})

// Every comment on the regime (schedule, task and introduction anchors) for the recipient
// regime-wide Comments tab.
export const getShareLinkRegimeComments = api(async function listShareLinkRegimeComments({ shareLinkId, regimeId }) {
    if (!shareLinkId || !regimeId) return []
    const result = await query(
        gql`
            query ListShareLinkRegimeComments($shareLinkId: String!, $regimeId: String!) {
                listShareLinkRegimeComments(shareLinkId: $shareLinkId, regimeId: $regimeId) {
                    comments {
                        _id
                        threadId
                        parentId
                        linkId
                        regimeId
                        scheduleId
                        schedulePath
                        scheduleTitle
                        content
                        authorScope
                        createdBy
                        authorName
                        versionLabel
                        created
                        modified
                        resolved
                        resolvedBy
                        resolvedAt
                        deleted
                        editedAt
                        selectionMetadata {
                            path
                            parentId
                            taskIndex
                            part
                            partLabel
                            start
                            end
                            selectedText
                            beforeText
                            afterText
                        }
                    }
                }
            }
        `,
        { shareLinkId, regimeId },
        { returns: "listShareLinkRegimeComments" }
    )
    return buildCommentThreads(result?.comments ?? [])
})

// Count of the recipient's own threads with unread replies / resolutions, for the Comments tab badge.
// Live-refreshes via the ShareLinkComment + read-state invalidation groups.
export const getShareLinkRecipientUnread = api(async function getShareLinkRecipientUnread({ shareLinkId, regimeId }) {
    if (!shareLinkId || !regimeId) return { total: 0, unreadThreadIds: [] }
    return query(
        gql`
            query GetShareLinkRecipientUnread($shareLinkId: String!, $regimeId: String!) {
                getShareLinkRecipientUnread(shareLinkId: $shareLinkId, regimeId: $regimeId) {
                    total
                    unreadThreadIds
                }
            }
        `,
        { shareLinkId, regimeId },
        { returns: "getShareLinkRecipientUnread" }
    )
})

export const markShareLinkCommentThreadsRead = api(async function markShareLinkCommentThreadsRead({
    shareLinkId,
    regimeId,
    threadIds,
}) {
    if (!shareLinkId || !regimeId || !threadIds?.length) return false
    return mutate(
        gql`
            mutation MarkShareLinkCommentThreadsRead(
                $shareLinkId: String!
                $regimeId: String!
                $threadIds: [String!]!
            ) {
                markShareLinkCommentThreadsRead(shareLinkId: $shareLinkId, regimeId: $regimeId, threadIds: $threadIds)
            }
        `,
        { shareLinkId, regimeId, threadIds },
        { returns: "markShareLinkCommentThreadsRead" }
    )
})

// Count of regime threads with unseen recipient comments / replies, for the internal (sharer) Comments
// tab badge. Live-refreshes via the ShareLinkComment + read-state invalidation groups.
export const getShareLinkRegimeUnread = api(async function getShareLinkRegimeUnread({ regimeId }) {
    if (!regimeId) return { total: 0, unreadThreadIds: [] }
    return query(
        gql`
            query GetShareLinkRegimeUnread($regimeId: String!) {
                getShareLinkRegimeUnread(regimeId: $regimeId) {
                    total
                    unreadThreadIds
                }
            }
        `,
        { regimeId },
        { returns: "getShareLinkRegimeUnread" }
    )
})

export const getShareLinkCommentContext = api(async function getShareLinkCommentContext({ commentId }) {
    if (!commentId) return null
    return query(
        gql`
            query GetShareLinkCommentContext($commentId: String!) {
                getShareLinkCommentContext(commentId: $commentId) {
                    commentId
                    threadId
                    shareLinkId
                    regimeId
                    scheduleId
                    schedulePath
                }
            }
        `,
        { commentId },
        { returns: "getShareLinkCommentContext" }
    )
})

export const markShareLinkRegimeCommentThreadsRead = api(async function markShareLinkRegimeCommentThreadsRead({
    regimeId,
    threadIds,
}) {
    if (!regimeId || !threadIds?.length) return false
    return mutate(
        gql`
            mutation MarkShareLinkRegimeCommentThreadsRead($regimeId: String!, $threadIds: [String!]!) {
                markShareLinkRegimeCommentThreadsRead(regimeId: $regimeId, threadIds: $threadIds)
            }
        `,
        { regimeId, threadIds },
        { returns: "markShareLinkRegimeCommentThreadsRead" }
    )
})

export const createShareLinkScheduleComment = api(async function createShareLinkComment(input) {
    return mutate(
        gql`
            mutation CreateShareLinkComment($input: CreateShareLinkCommentInput!) {
                createShareLinkComment(input: $input) {
                    _id
                }
            }
        `,
        { input },
        { returns: "createShareLinkComment" }
    )
})

export const replyToShareLinkScheduleComment = api(async function replyShareLinkComment(input) {
    return mutate(
        gql`
            mutation ReplyShareLinkComment($input: ReplyShareLinkCommentInput!) {
                replyShareLinkComment(input: $input) {
                    _id
                }
            }
        `,
        { input },
        { returns: "replyShareLinkComment" }
    )
})

export const resolveShareLinkScheduleComment = api(async function resolveShareLinkComment(input) {
    return mutate(
        gql`
            mutation ResolveShareLinkComment($input: ResolveShareLinkCommentInput!) {
                resolveShareLinkComment(input: $input) {
                    _id
                    resolved
                }
            }
        `,
        { input },
        { returns: "resolveShareLinkComment" }
    )
})

export const editShareLinkScheduleComment = api(async function editShareLinkComment(input) {
    return mutate(
        gql`
            mutation EditShareLinkComment($input: EditShareLinkCommentInput!) {
                editShareLinkComment(input: $input) {
                    _id
                    content
                    editedAt
                }
            }
        `,
        { input },
        { returns: "editShareLinkComment" }
    )
})

export const deleteShareLinkScheduleComment = api(async function deleteShareLinkComment(input) {
    return mutate(
        gql`
            mutation DeleteShareLinkComment($input: DeleteShareLinkCommentInput!) {
                deleteShareLinkComment(input: $input) {
                    _id
                    deleted
                }
            }
        `,
        { input },
        { returns: "deleteShareLinkComment" }
    )
})

export const getShareLinkCommentSummary = api(async function getShareLinkCommentSummary({ regimeId, facilityId } = {}) {
    if (!regimeId && !facilityId) return []
    return query(
        gql`
            query GetShareLinkCommentSummary($regimeId: String, $facilityId: String) {
                getShareLinkCommentSummary(regimeId: $regimeId, facilityId: $facilityId) {
                    shareLinkId
                    shareLinkName
                    regimeVersion
                    comments {
                        id
                        threadId
                        regimeId
                        scheduleId
                        schedulePath
                        scheduleRoutePath
                        scheduleTitle
                        scheduleCode
                        scheduleFolderId
                        scheduleLocation
                        draftSchedulePath
                        scheduleVersion
                        anchorType
                        targetId
                        taskIndex
                        taskLabel
                        part
                        partLabel
                        commentCount
                        authorName
                        authorUserId
                        authorType
                        recipientName
                        targetText
                        body
                        resolved
                        replyCount
                        updated
                    }
                }
            }
        `,
        { regimeId, facilityId },
        { returns: "getShareLinkCommentSummary" }
    )
})

export const resetShareLinkRegimeGatheredData = api(async function resetShareLinkRegimeGatheredData({
    regimeId,
    folderId,
}) {
    if (!regimeId) return { commentsRemoved: 0, responsesRemoved: 0 }
    return mutate(
        gql`
            mutation ResetShareLinkRegimeGatheredData($regimeId: String!, $folderId: String) {
                resetShareLinkRegimeGatheredData(regimeId: $regimeId, folderId: $folderId) {
                    commentsRemoved
                    responsesRemoved
                }
            }
        `,
        { regimeId, folderId },
        { returns: "resetShareLinkRegimeGatheredData" }
    )
})

export const exportShareLinkCommentsWorkbook = api(async function exportShareLinkCommentsWorkbook({
    regimeId,
    folderId,
} = {}) {
    if (!regimeId) return ""
    return query(
        gql`
            query ExportShareLinkCommentsWorkbook($regimeId: String!, $folderId: String) {
                exportShareLinkCommentsWorkbook(regimeId: $regimeId, folderId: $folderId)
            }
        `,
        { regimeId, folderId },
        { returns: "exportShareLinkCommentsWorkbook", fetchPolicy: "network-only" }
    )
})

function buildCommentThreads(comments) {
    const threadsById = new Map()
    const replies = []
    for (const comment of comments) {
        if (comment.parentId) {
            replies.push(normalizeComment(comment))
            continue
        }
        const thread = normalizeComment(comment)
        thread.replies = []
        threadsById.set(comment._id, thread)
        threadsById.set(comment.threadId, thread)
    }
    for (const reply of replies) {
        const thread = threadsById.get(reply.threadId)
        if (!thread) continue
        thread.replies.push(reply)
    }
    return [...new Set(threadsById.values())]
}

function normalizeComment(comment) {
    return {
        id: comment._id,
        threadId: comment.threadId,
        parentId: comment.parentId,
        shareLinkId: comment.linkId,
        regimeId: comment.regimeId,
        scheduleId: comment.scheduleId,
        schedulePath: comment.schedulePath,
        scheduleTitle: comment.scheduleTitle,
        authorUserId: comment.createdBy,
        authorType: getAuthorType(comment.authorScope),
        authorName: comment.authorName,
        body: comment.content,
        created: comment.created,
        modified: comment.modified,
        resolved: comment.resolved ?? false,
        resolvedBy: comment.resolvedBy,
        resolvedAt: comment.resolvedAt,
        deleted: comment.deleted ?? false,
        editedAt: comment.editedAt,
        anchor: buildAnchor(comment),
    }
}

function buildAnchor(comment) {
    const selectionMetadata = comment.selectionMetadata ?? {}
    const targetType = getAnchorType(selectionMetadata)
    return {
        type: targetType,
        scheduleId: comment.scheduleId,
        scheduleVersion: getVersionNumber(comment.versionLabel),
        targetId: selectionMetadata.parentId ?? comment.scheduleId,
        targetLabel: getAnchorLabel(comment, targetType, selectionMetadata),
        targetText: selectionMetadata.selectedText ?? comment.scheduleTitle ?? "",
        // Location breadcrumb ("Regime • Facility • Folder") and schedule code — filled in client-side
        // by the recipient Comments tab from the loaded regime tree (see share-link-recipient-comments-panel)
        location: "",
        code: "",
        taskIndex: selectionMetadata.taskIndex,
        part: selectionMetadata.part,
        partLabel: selectionMetadata.partLabel,
        path: selectionMetadata.path,
        start: selectionMetadata.start,
        end: selectionMetadata.end,
        beforeText: selectionMetadata.beforeText,
        afterText: selectionMetadata.afterText,
    }
}

function getAnchorType(selectionMetadata) {
    const pathRoot = selectionMetadata.path?.[0]
    if (pathRoot === "sharelink") return "sharelink"
    if (pathRoot === "folder") return "folder"
    if (pathRoot === "intro") return "intro"
    if (pathRoot === "tasks") return "task"
    return "schedule"
}

function getAnchorLabel(comment, targetType, selectionMetadata = {}) {
    if (targetType === "sharelink") return "Share link"
    if (targetType === "folder") return selectionMetadata.selectedText ?? "Folder"
    if (targetType === "intro") return "Introduction"
    return comment.scheduleTitle ?? "Schedule"
}

function getAuthorType(authorScope) {
    if (authorScope === "internal") return "internal"
    return "share-link"
}

function getVersionNumber(versionLabel) {
    const match = `${versionLabel ?? ""}`.match(/\d+/)
    if (!match) return undefined
    return Number.parseInt(match[0], 10)
}
